import {useSiteLanguage} from "../../career/SiteLanguage";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PersonnelPhoto from '../../career/PersonnelPhoto';
import axios from "axios";
import {
  AlertTriangle,
  BarChart3,
  BookOpen,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Clock3,
  FileText,
  MapPin,
  RefreshCw,
  Search,
  Users,
} from "lucide-react";
import "../../styles/mudur/EgitimDashboard.css";

const apiListesi = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.$values)) return value.$values;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.data?.$values)) return value.data.$values;
  return [];
};

const durumSinifi = (durum = "") => {
  const map = {
    "Tamamlandı": "tamamlandi",
    "Devam Ediyor": "devam-ediyor",
    "Başlamadı": "baslamadi",
    "Süresi Geçti": "suresi-gecti",
    "Atama Yok": "atama-yok",
  };
  return map[durum] || "atama-yok";
};

const tarihYaz = (value,lang='tr') => {
  if (!value) return "-";
  const tarih = new Date(value);
  if (Number.isNaN(tarih.getTime())) return "-";
  return tarih.toLocaleDateString(lang, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const metinKey = (value = "") =>
  String(value || "").trim().toLocaleLowerCase("tr-TR");

const personelKayitlari = (personel) => [
  ...apiListesi(personel?.egitimler),
  ...apiListesi(personel?.anketler),
  ...apiListesi(personel?.sinavlar),
];

const dashboardOzetHesapla = (personeller) => {
  const kayitlar = personeller.flatMap(personelKayitlari);
  const tamamlandi = kayitlar.filter((kayit) => kayit.durum === "Tamamlandı").length;

  return {
    personelSayisi: personeller.length,
    toplamAtama: kayitlar.length,
    tamamlandi,
    devamEdiyor: kayitlar.filter((kayit) => kayit.durum === "Devam Ediyor").length,
    baslamadi: kayitlar.filter((kayit) => kayit.durum === "Başlamadı").length,
    suresiGecti: kayitlar.filter((kayit) => kayit.durum === "Süresi Geçti").length,
    tamamlanmaOrani:
      kayitlar.length === 0
        ? 0
        : Math.round((tamamlandi * 1000) / kayitlar.length) / 10,
  };
};

const turIkonu = (tur) => {
  if (tur === "Eğitim") return <BookOpen size={18} />;
  if (tur === "Anket") return <ClipboardList size={18} />;
  return <FileText size={18} />;
};

export default function EgitimDashboard() {
  const {tx,lang} = useSiteLanguage();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [arama, setArama] = useState("");
  const [tur, setTur] = useState("Tümü");
  const [durum, setDurum] = useState("Tümü");
  const [seciliMagazaId, setSeciliMagazaId] = useState("");
  const [acikPersoneller, setAcikPersoneller] = useState(() => new Set());
  const dashboardRequest = useRef(null);

  const dashboardGetir = useCallback(async () => {
    dashboardRequest.current?.abort();
    const controller = new AbortController();
    dashboardRequest.current = controller;
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/mudur/egitim-dashboard`,
        {
          signal: controller.signal,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setDashboard(response.data?.data || null);
    } catch (err) {
      if (controller.signal.aborted) return;
      if (err.response?.status === 403) {
        setError("Bu ekran yalnızca yetkili mağaza ve alan yöneticilerine açıktır.");
      } else {
        setError(
          err.response?.data?.message ||
            "Eğitim durumu alınamadı. Lütfen tekrar deneyin."
        );
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    dashboardGetir();
    return () => dashboardRequest.current?.abort();
  }, [dashboardGetir]);

  const personeller = useMemo(
    () => apiListesi(dashboard?.personeller),
    [dashboard]
  );

  const magazalar = useMemo(
    () => apiListesi(dashboard?.magazalar),
    [dashboard]
  );

  useEffect(() => {
    if (magazalar.length === 0) {
      setSeciliMagazaId("");
      return;
    }

    setSeciliMagazaId((onceki) => {
      const secimGecerli =
        onceki === "tum" ||
        magazalar.some((magaza) => String(magaza.magazaId) === String(onceki));
      return secimGecerli ? onceki : String(magazalar[0].magazaId);
    });
  }, [magazalar]);

  const kapsamdakiPersoneller = useMemo(() => {
    if (!seciliMagazaId || seciliMagazaId === "tum") return personeller;
    return personeller.filter(
      (personel) => String(personel.magazaId) === String(seciliMagazaId)
    );
  }, [personeller, seciliMagazaId]);

  const filtreliPersoneller = useMemo(() => {
    const aramaKey = metinKey(arama);

    return kapsamdakiPersoneller
      .map((personel) => {
        const tumKayitlar = personelKayitlari(personel);
        const personelEslesiyor = [
          personel.adSoyad,
          personel.pozisyon,
          personel.magazaAdi,
        ].some((value) => metinKey(value).includes(aramaKey));

        const gorunenKayitlar = tumKayitlar.filter((kayit) => {
          const turUygun = tur === "Tümü" || kayit.tur === tur;
          const durumUygun = durum === "Tümü" || kayit.durum === durum;
          const metinUygun =
            !aramaKey ||
            personelEslesiyor ||
            metinKey(kayit.baslik).includes(aramaKey);
          return turUygun && durumUygun && metinUygun;
        });

        const kayitsizPersonelGoster =
          tumKayitlar.length === 0 &&
          tur === "Tümü" &&
          durum === "Tümü" &&
          (!aramaKey || personelEslesiyor);

        if (gorunenKayitlar.length === 0 && !kayitsizPersonelGoster) return null;
        return { ...personel, gorunenKayitlar };
      })
      .filter(Boolean);
  }, [arama, durum, kapsamdakiPersoneller, tur]);

  const ozet = useMemo(
    () => dashboardOzetHesapla(kapsamdakiPersoneller),
    [kapsamdakiPersoneller]
  );

  const seciliMagazaAdi = useMemo(() => {
    if (seciliMagazaId === "tum") return `${magazalar.length} mağaza`;
    return (
      magazalar.find(
        (magaza) => String(magaza.magazaId) === String(seciliMagazaId)
      )?.magazaAdi || dashboard?.magazaAdi || "Mağazam"
    );
  }, [dashboard, magazalar, seciliMagazaId]);

  const personelAcKapat = (kullaniciId) => {
    setAcikPersoneller((onceki) => {
      const yeni = new Set(onceki);
      if (yeni.has(kullaniciId)) yeni.delete(kullaniciId);
      else yeni.add(kullaniciId);
      return yeni;
    });
  };

  if (loading) {
    return (
      <div className="egitim-dashboard-loading">
        <RefreshCw className="egitim-dashboard-spin" size={30} />
        <span>{tx("Mağaza eğitim durumu hazırlanıyor...")}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="egitim-dashboard-error">
        <AlertTriangle size={34} />
        <h2>{tx("Dashboard açılamadı")}</h2>
        <p>{error}</p>
        <button type="button" onClick={dashboardGetir}>{tx("Tekrar dene")}</button>
      </div>
    );
  }

  const ozetKartlari = [
    {
      label: "Personel",
      value: ozet.personelSayisi || 0,
      icon: <Users size={22} />,
      tone: "lacivert",
    },
    {
      label: "Toplam İçerik",
      value: ozet.toplamAtama || 0,
      icon: <BarChart3 size={22} />,
      tone: "mor",
    },
    {
      label: "Tamamlandı",
      value: ozet.tamamlandi || 0,
      icon: <CheckCircle2 size={22} />,
      tone: "yesil",
    },
    {
      label: "Devam Ediyor",
      value: ozet.devamEdiyor || 0,
      icon: <Clock3 size={22} />,
      tone: "mavi",
    },
    {
      label: "Başlamadı",
      value: ozet.baslamadi || 0,
      icon: <BookOpen size={22} />,
      tone: "gri",
    },
    {
      label: "Süresi Geçti",
      value: ozet.suresiGecti || 0,
      icon: <AlertTriangle size={22} />,
      tone: "kirmizi",
    },
  ];

  return (
    <div className="personel-icerik egitim-dashboard-page">
        <header className="egitim-dashboard-hero">
          <div>
            <span className="egitim-dashboard-eyebrow">{tx("MAĞAZA EKİP TAKİBİ")}</span>
            <h2>Dashboard</h2>
            <p>{tx("Ekibinizin eğitim, anket ve sınav süreçlerini tek ekrandan takip edin.")}</p>
            <div className="egitim-dashboard-location">
              <MapPin size={17} />
              <span>{seciliMagazaAdi}</span>
            </div>
          </div>
          <div className="egitim-dashboard-rate">
            <span>{tx("Tamamlanma oranı")}</span>
            <strong>%{Number(ozet.tamamlanmaOrani || 0).toLocaleString(lang)}</strong>
            <div className="egitim-dashboard-rate-track">
              <span
                style={{
                  width: `${Math.min(100, Number(ozet.tamamlanmaOrani || 0))}%`,
                }}
              />
            </div>
          </div>
        </header>

        <section className="egitim-dashboard-summary" aria-label={tx("Durum özeti")}>
          {ozetKartlari.map((kart) => (
            <article key={kart.label} className={`egitim-summary-card ${kart.tone}`}>
              <div className="egitim-summary-icon">{kart.icon}</div>
              <div>
                <strong>{kart.value}</strong>
                <span>{tx(kart.label)}</span>
              </div>
            </article>
          ))}
        </section>

        <section className="egitim-dashboard-content">
          <div className="egitim-dashboard-toolbar">
            <div>
              <h2>{tx("Personel durumu")}</h2>
              <p>{filtreliPersoneller.length} {tx("personel listeleniyor")}</p>
            </div>
            <button
              type="button"
              className="egitim-dashboard-refresh"
              onClick={dashboardGetir}
              title={tx("Verileri yenile")}
            >
              <RefreshCw size={18} />{tx("Yenile")}</button>
          </div>

          <div
            className={`egitim-dashboard-filters ${
              magazalar.length > 1 ? "magaza-secimli" : ""
            }`}
          >
            {magazalar.length > 1 && (
              <label className="egitim-dashboard-store-filter">
                <Building2 size={18} />
                <select
                  value={seciliMagazaId}
                  onChange={(event) => setSeciliMagazaId(event.target.value)}
                  aria-label={tx("Mağaza seç")}
                >
                  <option value="tum">{tx("Tüm mağazalar")}</option>
                  {magazalar.map((magaza) => (
                    <option key={magaza.magazaId} value={String(magaza.magazaId)}>
                      {magaza.magazaAdi}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="egitim-dashboard-search">
              <Search size={19} />
              <input
                value={arama}
                onChange={(event) => setArama(event.target.value)}
                placeholder={tx("Personel veya atama ara")}
              />
            </label>
            <select value={tur} onChange={(event) => setTur(event.target.value)}>
              <option value="Tümü">{tx("Tümü")}</option>
              <option value="Eğitim">{tx("Eğitim")}</option>
              <option value="Anket">{tx("Anket")}</option>
              <option value="Sınav">{tx("Sınav")}</option>
            </select>
            <select value={durum} onChange={(event) => setDurum(event.target.value)}>
              <option value="Tümü">{tx("Tümü")}</option>
              <option value="Tamamlandı">{tx("Tamamlandı")}</option>
              <option value="Devam Ediyor">{tx("Devam Ediyor")}</option>
              <option value="Başlamadı">{tx("Başlamadı")}</option>
              <option value="Süresi Geçti">{tx("Süresi Geçti")}</option>
            </select>
          </div>

          <div className="egitim-personel-listesi">
            {filtreliPersoneller.map((personel) => {
              const acik = acikPersoneller.has(personel.kullaniciId);
              const basHarfler = String(personel.adSoyad || "P")
                .split(" ")
                .filter(Boolean)
                .slice(0, 2)
                .map((parca) => parca[0])
                .join("")
                .toLocaleUpperCase("tr-TR");
              return (
                <article className="egitim-personel-card" key={personel.kullaniciId}>
                  <button
                    type="button"
                    className="egitim-personel-head"
                    onClick={() => personelAcKapat(personel.kullaniciId)}
                    aria-expanded={acik}
                  >
                    <div className="egitim-personel-identity">
                      <PersonnelPhoto className="egitim-personel-avatar" endpoint={personel.profilFotoEndpoint}
                        name={personel.adSoyad} initials={basHarfler} />
                      <span>
                        <strong>{personel.adSoyad}</strong>
                        <small>{personel.pozisyon} · {personel.magazaAdi}</small>
                      </span>
                    </div>
                    <div className="egitim-personel-counts">
                      <span><b>{personel.ozet?.tamamlandi || 0}</b>{tx("Tamam")}</span>
                      <span><b>{personel.ozet?.devamEdiyor || 0}</b>{tx("Devam")}</span>
                      <span><b>{personel.ozet?.baslamadi || 0}</b>{tx("Başlamadı")}</span>
                    </div>
                    <span className={`egitim-status-badge ${durumSinifi(personel.genelDurum)}`}>
                      {tx(personel.genelDurum)}
                    </span>
                    <span className="egitim-personel-chevron">
                      {acik ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </span>
                  </button>

                  {acik && (
                    <div className="egitim-personel-details">
                      {personel.gorunenKayitlar.length === 0 ? (
                        <div className="egitim-personel-empty">{tx("Bu personele henüz eğitim, anket veya sınav atanmamış.")}</div>
                      ) : (
                        personel.gorunenKayitlar.map((kayit) => (
                          <div
                            className="egitim-assignment-row"
                            key={`${kayit.tur}-${kayit.kaynak || "platform"}-${kayit.atamaId}`}
                          >
                            <div className={`egitim-assignment-icon ${metinKey(kayit.tur)}`}>
                              {turIkonu(kayit.tur)}
                            </div>
                            <div className="egitim-assignment-main">
                              <span className="egitim-assignment-type">
                                {tx(kayit.tur)}{kayit.kaynak ? ` · ${kayit.kaynak}` : ""}
                              </span>
                              <strong>{kayit.baslik}</strong>
                              {kayit.tur === "Eğitim" && kayit.ilerlemeYuzdesi !== null && (
                                <div className="egitim-assignment-progress">
                                  <span><i style={{ width: `${kayit.ilerlemeYuzdesi}%` }} /></span>
                                  <small>%{kayit.ilerlemeYuzdesi}</small>
                                </div>
                              )}
                            </div>
                            <div className="egitim-assignment-dates">
                              <span>
                                {kayit.kaynak === "İSG" ? tx("Başlangıç") : "Atama"}{" "}
                                <b>{tarihYaz(kayit.atamaTarihi,lang)}</b>
                              </span>
                              {kayit.kaynak !== "İSG" && (
                                <span>{tx("Son tarih")} <b>{tarihYaz(kayit.sonTarih,lang)}</b></span>
                              )}
                              {kayit.sonHareketTarihi && (
                                <span>{tx("Son hareket")} <b>{tarihYaz(kayit.sonHareketTarihi,lang)}</b></span>
                              )}
                            </div>
                            <span className={`egitim-status-badge ${durumSinifi(kayit.durum)}`}>
                              {tx(kayit.durum)}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </article>
              );
            })}

            {filtreliPersoneller.length === 0 && (
              <div className="egitim-dashboard-empty">
                <Search size={30} />
                <h3>{tx("Sonuç bulunamadı")}</h3>
                <p>{tx("Arama veya filtre seçiminizi değiştirip tekrar deneyin.")}</p>
              </div>
            )}
          </div>
        </section>
    </div>
  );
}
